import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import * as d3 from 'd3'
import type * as dateish from 'js/dateish';

import type { Location, Observation } from "js/ebird/ebird";
import { obsFilterRegions, type Region } from 'js/ebird/ebird';
import type { RootState } from 'web/store';
import type { GeographyDB } from 'web/db';
import type { Barchart } from 'js/ebird/barcharts';

export interface AppState { // TODO: Split out the different states
  allObservations: Observation[] // TODO better way to do import check

  filter: {
    regions: Region[]
    // TODO: timeframe?
  }

  currentRegion: Region|null
  barcharts: Record<string, BarchartRegionState>
  // TODO: Store the color theme?
}

type BarchartRegionState =
  | { status: 'idle' | 'loading' }
  | { status: 'ok', barchart: Barchart }
  | { status: 'error', error: string }

const initialState: AppState = {
  allObservations: [],
  filter: {
    regions: [
      // TODO: expose this in the UI { code: 'US-WA', name: 'Washington', kind: 'state' }
    ]
  },
  // TODO: Set this as part of the initial load of the app to trigger the fetch
  currentRegion: null,
  barcharts: {}
};

// TODO: Figure out the async parts
export const fetchBarchart = createAsyncThunk(
  'app/fetchBarchart',
  async (region: string, { getState, rejectWithValue }) => {
    const state = getState() as RootState;
    const current = state.app.barcharts[region];
    if (current?.status === 'ok') {
      return { region, data: current.barchart };
    }

    try {
      const response = await fetch(`/data/barcharts/${region}.json`);
      if (!response.ok) {
        return rejectWithValue(`Error ${response.status}: ${response.statusText}`);
      }
      const data = await response.json();
      return { region, data };
    } catch (e) {
      return rejectWithValue((e as Error).message);
    }
  }
);

const appSlice = createSlice({
  name: 'app',
  initialState,
  reducers: {
    initObservations: (state, { payload }: PayloadAction<Observation[]>) => {
      state.allObservations = payload;
    },

    setCurrentRegion: (state, { payload }: PayloadAction<Region>) => {
      state.currentRegion = payload;
      if (!state.barcharts[payload.code]) {
        state.barcharts[payload.code] = { status: 'idle' };
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchBarchart.pending, (state, action) => {
        const region = action.meta.arg;
        state.barcharts[region] = { status: 'loading' };
      })
      .addCase(fetchBarchart.fulfilled, (state, action) => {
        const { region, data } = action.payload;
        state.barcharts[region] = { status: 'ok', barchart: data };
      })
      .addCase(fetchBarchart.rejected, (state, action) => {
        const region = action.meta.arg;
        state.barcharts[region] = { status: 'error', error: action.payload as string };
      });
  },
});

export const getAllObservations = (state: RootState) => state.app.allObservations;
// TODO: Better name for this...
export const getFilterRegions = (state: RootState) => state.app.filter.regions;

const filterObsDoY = (all: Observation[], doy: dateish.DoY) => {
  return all.filter(o => `${o.date.month}-${o.date.day}` === doy);
};

const filterObsMoY = (all: Observation[], moy: dateish.MoY) => {
  return all.filter(o => o.date.month === moy);
};

export const getObservationsDoY = createSelector(
  [ getAllObservations, (_: RootState, doy: dateish.DoY) => doy ],
  filterObsDoY
);

export const getObservationsMoY = createSelector(
  [ getAllObservations, (_: RootState, moy: dateish.MoY) => moy ],
  filterObsMoY
);

export const getObservationsDateish = createSelector(
  [ getAllObservations, (_: RootState, date: dateish.Any) => date ],
  (all: Observation[], date: dateish.Any) => {
    return date.kind === 'doy' ? filterObsDoY(all, date.value) : filterObsMoY(all, date.value);
  }
);

export const getAllChecklists = createSelector(
  [ getAllObservations ],
  (all: Observation[]) => {
    const checklists = d3.rollup(all, obs => obs[0].checklist, o => o.checklist.id);
    return Array.from(checklists.values());
  }
);

export const getAllLocations = createSelector( // TODO: These are hotspots
  [ getAllObservations ],
  (all: Observation[]) => {
    // TODO: This is a reduce to uniq rollup just like checklists
    const locations = d3.rollup(all, obs => obs[0].location, o => o.location.id);
    return Array.from(locations.values());
  }
);

export const getRegionFilterChoices = createSelector(
  [
    (state) => getAllLocations(state),
    (_, geo: GeographyDB) => geo,
  ],
  (locations: Location[], geo: GeographyDB) => geo.filterByLocations(locations)
);

export const getRegionalBarchart = createSelector(
  [
    (state) => state.app.currentRegion,
    (state) => state.app.barcharts,
  ],
  (region: Region, barcharts: Record<string, BarchartRegionState>): Barchart | null => {
    const data = barcharts[region.code];
    return data?.status === 'ok' ? data.barchart : null;
  }
);


// Get observations based on the current filter
export const getObservations = createSelector(
  [ getAllObservations, getFilterRegions ],
  obsFilterRegions
);

export default appSlice;
